import React, { useCallback, useMemo, Fragment, useEffect, useState } from 'react';
import { Button, ListGroup } from 'react-bootstrap';
import TreeMenu, { ItemComponent, TreeMenuProps, TreeNodeInArray } from 'react-simple-tree-menu';
import { useField } from 'formik';
import { FormField } from '../../../types/general';
import * as FieldsProps from '../../../types/fields';
import 'react-simple-tree-menu/dist/main.css';

export type Props = FormField;

const SAMPLE_OPTIONS = [
	{
		value: 'a1',
		text: 'Nodo 1',
		parent: null,
	},
	{
		value: 'a11',
		text: 'Figlio di 1',
		parent: 'a1',
	},
	{
		value: 'b1',
		text: 'Nodo 2',
		parent: null,
	},
	{
		value: 'b11',
		text: 'Figlio di 2',
		parent: 'b1',
	},
	{
		value: 'b111',
		text: 'Nipote di 2',
		parent: 'b11',
	},
] as FieldsProps.TreeOption[];

/**
 * Tree view
 */
const Tree: React.FC<Props> = (props) => {
	const { schema, options = {} } = props;
	const { name, props: fieldProps = {} } = schema;
	const { editMode } = options;
	const { items: realItems = [], readonly, disabled, creationURL, onRefresh } = fieldProps as FieldsProps.Tree;
	const items = editMode ? SAMPLE_OPTIONS : realItems;
	const [focus, setFocus] = useState(false);
	const [field, meta, helpers] = useField(name);

	const { value } = field;
	const { setValue } = helpers;

	// methods
	const getChildren = (
		items: FieldsProps.TreeOption[],
		parent: null | FieldsProps.SelectOptionValue
	): TreeNodeInArray[] => {
		const tree = items
			.filter((item) => item.parent === parent)
			.map((item) => {
				const children = getChildren(items, item.value);
				return {
					key: item.value,
					label: item.text,
					nodes: children,
					...item,
				} as TreeNodeInArray;
			});
		return tree;
	};

	const findPath = (items: FieldsProps.TreeOption[], value: FieldsProps.SelectOptionValue) => {
		const path = items.reduce((acc, item) => {
			if (item.value === value) {
				return item.parent ? `${findPath(items, item.parent)}/${item.value}` : `${item.value}`;
			}
			return acc;
		}, '') as string;
		return path;
	};

	const tree = useMemo(() => getChildren(items, null), [items]);
	const currentValue = useMemo<string>(() => findPath(items, value), [items, value]);
	return (
		<div key={`field-${field?.name}`} className={`Field-tree ${focus ? 'focus' : ''}`}>
			<TreeMenu
				data={tree}
				debounceTime={125}
				initialActiveKey={currentValue}
				initialFocusKey={currentValue}
				initialOpenNodes={currentValue
					.split('/')
					.reduce<string[]>((acc, pt) => [...acc, [...acc, pt].join('/')], [])}
				onClickItem={(node) => {
					setValue(node.value);
				}}
			>
				{({ search, items }) => (
					<Fragment>
						{search && (
							<input
								className='rstm-search'
								aria-label='Cerca'
								type='search'
								placeholder='Cerca...'
								onChange={(e) => search(e.target.value)}
							/>
						)}
						<ul className='rstm-tree-item-group'>
							{items.map(({ key, ...props }) => (
								<ItemComponent key={key} {...props} />
							))}
						</ul>
					</Fragment>
				)}
			</TreeMenu>
			<div className='hstack gap-2 mt-1'>
				<div>
					{onRefresh && (
						<Button variant='outline-secondary' size='sm' onClick={onRefresh}>
							<i className='bi bi-arrow-clockwise' /> Aggiorna
						</Button>
					)}
				</div>
				<div className='ms-auto'>
					{creationURL && typeof creationURL === 'function' && creationURL()}
					{creationURL && typeof creationURL === 'string' && (
						<Fragment>
							<Button variant='outline-primary' size='sm' href={creationURL} target='_blank'>
								<i className='bi bi-plus' /> Nuovo
							</Button>
						</Fragment>
					)}
				</div>
			</div>
		</div>
	);
};

export default Tree;
